import { FC, useEffect, useState } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Navbar from "@/components/navbar/navbar";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "@/shared/components/header-items/HeaderItems";
import DashboardContentLayout from "@/shared/layouts/DashboardContentLayout";
import EmptyState from "@/shared/components/EmptyState";
import useUnsavedChangesBlocker from "@/shared/hooks/useUnsavedChangesBlocker";
import { AdRoutes } from "@/constants/ad-routes";
import { AdminOrganizationDetailResDto } from "service/api/estabrookSchemas";
import useOrganizationDetail from "../hooks/useOrganizationDetail";
import useEditOrganizationForm from "../hooks/useEditOrganizationForm";
import useOrganizationAccessToggle from "../hooks/useOrganizationAccessToggle";
import EditOrganizationForm from "../components/EditOrganizationForm";
import OrganizationAccessConfirmDialog from "../components/OrganizationAccessConfirmDialog";

interface EditOrganizationPageContentProps {
  organization: AdminOrganizationDetailResDto;
}

const EditOrganizationPageContent: FC<EditOrganizationPageContentProps> = ({
  organization,
}) => {
  const navigate = useNavigate();
  const [isSaved, setIsSaved] = useState(false);
  const detailUrl = AdRoutes.organizationDetail.replace(
    ":organizationId",
    organization.id,
  );

  const {
    organizationForm,
    homeImageError,
    clearHomeImageError,
    isLoading,
    placePredictions,
    getPlacePredictions,
    getAddressComponents,
  } = useEditOrganizationForm(organization, () => {
    toast.success("Organization Info Updated Successfully");
    setIsSaved(true);
  });
  const {
    pendingOrganization,
    isSubmitting: isTogglingAccess,
    requestToggle,
    confirmToggle,
    cancelToggle,
  } = useOrganizationAccessToggle();

  useUnsavedChangesBlocker(organizationForm.dirty && !isSaved);

  useEffect(() => {
    if (isSaved) navigate(detailUrl);
  }, [isSaved, navigate, detailUrl]);

  return (
    <DashboardContentLayout horizontalPadding="40px">
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              { label: "Manage Organizations", url: AdRoutes.organizations },
              { label: organization.name, url: detailUrl },
              { label: "Edit Organization Info" },
            ]}
          />
          <HeaderItemsRight>
            <Button
              variant="contained"
              disabled={
                isLoading ||
                (organizationForm.values.showPromotionalBanner &&
                  Boolean(homeImageError)) ||
                !organizationForm.dirty ||
                !organizationForm.isValid
              }
              onClick={() => organizationForm.submitForm()}
              data-testid="save-organization-changes"
            >
              Save Changes
            </Button>
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>
      <EditOrganizationForm
        organization={organization}
        organizationForm={organizationForm}
        homeImageError={homeImageError}
        onHomeImageErrorDismiss={clearHomeImageError}
        isActive={organization.isActive}
        onToggleAccessRequest={() => requestToggle(organization)}
        placePredictions={placePredictions}
        getPlacePredictions={getPlacePredictions}
        getAddressComponents={getAddressComponents}
      />
      <OrganizationAccessConfirmDialog
        organization={pendingOrganization}
        isSubmitting={isTogglingAccess}
        onConfirm={confirmToggle}
        onCancel={cancelToggle}
      />
    </DashboardContentLayout>
  );
};

const EditOrganizationPage: FC = () => {
  const { organizationId = "" } = useParams<{ organizationId: string }>();
  const { organization, isLoading, isError, refetch } =
    useOrganizationDetail(organizationId);

  if (isLoading) {
    return (
      <DashboardContentLayout horizontalPadding="40px">
        <Navbar showAccountMenu />
        <Box mt="40px" display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </DashboardContentLayout>
    );
  }

  if (isError || !organization) {
    return (
      <DashboardContentLayout horizontalPadding="40px">
        <Navbar showAccountMenu />
        <Box mt="20px">
          <EmptyState
            title="Couldn't load organization"
            description="Something went wrong while loading this organization. Please try again."
            actionLabel="Retry"
            onAction={() => refetch()}
          />
        </Box>
      </DashboardContentLayout>
    );
  }

  return <EditOrganizationPageContent organization={organization} />;
};

export default EditOrganizationPage;
